import mapboxgl from 'mapbox-gl'
import carTopSvg from '@img/map-car.svg?raw'

/**
 * THE DRIVER'S CAR AS AN ACTUAL 3D OBJECT — a Mapbox custom layer holding a car whose PLAN
 * is `map-car.svg` and whose SIDE is a profile defined here, drawn in place of the flat
 * sprite whenever the camera tilts.
 *
 * ── Why a custom layer and not a marker ───────────────────────────────────────────
 * A `mapboxgl.Marker` is a DOM node the map moves around. `pitchAlignment: 'map'` lays it
 * flat ON the road, which is the best a picture can do: at 55° of tilt the artwork is a
 * foreshortened smear on the tarmac with no height to it, because it has no height.
 *
 * ── ⚠️ THE PLAN IS NOT ENOUGH, AND THIS IS THE LESSON THAT COST TWO REWRITES ──────
 * The first attempt was a hand-made glTF. Rejected, correctly: it was A car, not THE car —
 * a second drawing of the same vehicle, so the 2D/3D toggle swapped between two products.
 *
 * The second ran the SVG outline through `ExtrudeGeometry` — the exact silhouette, pushed
 * straight up. In plan view it matched the sprite perfectly, which is what made it look
 * like the answer. Tilted, it was a bar of soap: a rounded lump with no shoulder, no glass
 * and no roofline. A top-down drawing contains NO information about the side of a car, and
 * an extrusion invents none.
 *
 * What a car looks like from 55° is almost entirely side. So the outline supplies the PLAN —
 * the half-width at every station along the car, which is what keeps the footprint exactly
 * the sprite's — and `ROOFLINE` and `UNDERSIDE` supply the SECTION. Cross-sections are
 * swept between them, giving the things that actually say "car":
 *
 *   a long bonnet and a short boot, from the roofline dropping away either side of the cabin
 *   a raked windscreen, from how fast it drops
 *   a near-black glass band under a light roof — the strongest single cue of the three
 *   four wheels, which put the car ON the road rather than hovering over it
 *
 * ── Why not a Mapbox `model` layer ────────────────────────────────────────────────
 * Mapbox GL JS v3 draws glTF natively, but only on the Standard style — it needs the 3D
 * lighting the classic styles do not carry, and `/config/app` serves `light-v10` to every
 * screen in the portal.
 *
 * ── ⚠️ THREE IS LOADED LAZILY, AND THAT IS THE POINT ──────────────────────────────
 * `three` is ~730KB before gzip and only a tilted map ever needs it. It and the loader are
 * behind `import()` inside this factory, so a driver who never tilts never downloads
 * either — the dashboard, the trip list and every 2D map stay exactly the size they were.
 */

/** Layer id, exported so the caller can check for it before adding a second one. */
export const CAR_MODEL_LAYER_ID = 'driver-car-3d'

/**
 * WHICH WAY THE ARTWORK'S NOSE POINTS, in degrees clockwise from north, before anything
 * rotates it — the same measurement `RouteMap` applies to the flat marker, for the same
 * reason: `map-car.svg` is drawn on a ~21° slant with its nose down the page.
 *
 * ⚠️ KEPT IN STEP WITH `CAR_NOSE_DEG` IN `RouteMap`. They are one fact about one file. If
 * the artwork is ever re-drawn straight, both become 0 together — and if only one is
 * updated, the 2D and 3D cars point 21° apart and the toggle looks broken.
 *
 * ⚠️ AND IT IS THE HIGH-`y` END OF THE FILE THAT IS THE FRONT. The two white dots in the
 * artwork are wing mirrors: they sit as a symmetric pair 2 units short of one end, and that
 * is the end this bearing points at. It is the check to repeat if the car ever drives
 * backwards — and everything below assumes it, because the bonnet is longer than the boot.
 */
const CAR_NOSE_DEG = 159

/**
 * THE SIDE PROFILE — the top of the car at each station along it, as `[x, z]` in fractions
 * of the car's own length. `x` runs −0.5 at the tail to +0.5 at the nose; `z` is height off
 * the road. Interpolated smoothly, so a dozen points describe the whole shape.
 *
 * ⚠️ THIS IS THE ONLY PART OF THE CAR THAT IS NOT THE ARTWORK, and it carries everything
 * the artwork cannot say. Read it as a silhouette: 0.104 at the nose rising over a long
 * bonnet to 0.163, then the windscreen — 0.163 to 0.250 across a tenth of the car, which is
 * the rake — a flat roof to −0.205, the backlight dropping to 0.176, a short boot deck and
 * a tail at 0.128.
 *
 * ⚠️ THE CABIN IS BEHIND CENTRE AND THE BONNET IS LONG. That asymmetry is the whole reason
 * a driver can tell at a glance which way their own car points, and it is the first thing to
 * check if the car ever looks wrong rather than the colours.
 */
const ROOFLINE = [
    [-0.500, 0.128], [-0.440, 0.150], [-0.330, 0.153], [-0.272, 0.176],
    [-0.205, 0.248], [0.000, 0.258], [0.055, 0.250], [0.128, 0.163],
    [0.230, 0.148], [0.380, 0.138], [0.460, 0.120], [0.500, 0.104],
]

/** The underside, same units. Lifted at both ends so the car has overhangs, not a skirt. */
const UNDERSIDE = [
    [-0.500, 0.052], [-0.420, 0.028], [0.400, 0.028], [0.500, 0.050],
]

/**
 * THE BELT LINE — where the painted body stops and the glass starts.
 *
 * Everything below is body; everything the roofline puts above it is greenhouse. That one
 * comparison is what carves the cabin out of the profile, so the glass automatically covers
 * exactly the stretch where the roofline is high and nothing has to name the cabin's extent
 * twice.
 */
const BELT_Z = 0.150

/** The greenhouse is drawn in from the body's flanks, and the roof plate further still. */
const GLASS_INSET = 0.80
const ROOF_INSET = 0.58

/** How high the roofline has to be for the light roof plate to cover it — the flat top. */
const ROOF_MIN_Z = 0.236

/** Wheels, in fractions of length. `yInset` is how far in from the flank they sit. */
const WHEEL = {radius: 0.058, width: 0.030, axleX: 0.295, yInset: 0.92, sides: 12}

/**
 * How the swept cross-section is shaped: a superellipse of `SECTION_POWER`, sampled
 * `SECTION_SIDES` times. 3.4 is a rounded rectangle — square enough to have a flank and a
 * roof, round enough that neither meets the other at a hard corner.
 */
const SECTION_SIDES = 18
const SECTION_POWER = 3.4

/** Stations along the car. 48 is smooth at every zoom and still only ~3,000 triangles. */
const STATIONS = 48

/** How finely each curve of the SVG outline is sampled to get the plan. */
const CURVE_SAMPLES = 6

/**
 * The four greys, and the one doing the work.
 *
 * Body and roof are the artwork's own middle gradient stops, so the car is the colour it
 * always was. `GLASS` is not in the artwork at all — the sprite has no glass, because from
 * directly overhead there is nothing to see. It is near-black on purpose: the band of
 * shadow under a roofline is the strongest single cue that a shape is a vehicle, and
 * without it the whole thing collapses back into a lump.
 */
const BODY_COLOR = 0x484d54
const GLASS_COLOR = 0x191c21
const ROOF_COLOR = 0x9aa0a7
const WHEEL_COLOR = 0x101216

/**
 * HOW LONG THE CAR IS DRAWN ON THE MAP, in metres of real ground.
 *
 * ⚠️ NOT 4.5m, AND NOT AN ACCIDENT. At the cruise zoom of 18.5 the ground runs about
 * 0.18 m/px, so a life-size car is 25px — technically correct and hard to pick out of a
 * junction. The flat sprite it replaces is 44px, or 8m of ground at that zoom, which was
 * this number until the 3D car was seen on a real map and read as too small.
 *
 * ⚠️ IT IS BIGGER THAN THE SPRITE ON PURPOSE, WHICH THE SPRITE'S OWN 8m ARGUMENT MISSES.
 * The sprite fills its 44px: it is a flat plate seen face on, and every one of those pixels
 * is car. This is a solid seen at 55°, so it is foreshortened to roughly two thirds of its
 * length on screen and its own roof hides part of its body. Matching the sprite's ground
 * footprint therefore does NOT match its presence — it lands visibly smaller. 11m is the
 * size at which the two read alike through the toggle.
 */
const DRAWN_LENGTH_M = 11

/**
 * A FLOOR ON THE CAR'S SCREEN SIZE, in pixels of length.
 *
 * The car is anchored to the GROUND — that is what makes it sit ON the road rather than
 * float over it — which means it shrinks with every zoom out, and on a map fitted to a
 * 10km ride it would be a fraction of a pixel. The sprite has the opposite behaviour: 44px
 * at every zoom. So a driver who tilts a fully-framed route would swap a visible car for
 * nothing at all. Below this the car stops shrinking, which at that zoom is the only way it
 * is drawn at all.
 *
 * Raised with `DRAWN_LENGTH_M` and for the same reason: a tilted solid needs more pixels
 * than a flat plate to read as the same size.
 */
const MIN_SCREEN_PX = 34

/**
 * WHICH CONTOUR OF THE ARTWORK IS THE PLAN, picked by fill rather than by position.
 *
 * `map-car.svg` holds eleven paths — body, inner shading, the dark outline ring, two mirror
 * dots drawn twice each for their stroke — and indexing into that list means an artwork edit
 * that adds a highlight silently rebuilds the car out of the wrong piece. The outer
 * silhouette carries a gradient with a stable id, so the id is the handle.
 */
const HULL_FILL = 'paint0_'

/**
 * Ground metres per screen pixel at a given zoom and latitude.
 *
 * ⚠️ 512, NOT 256. Mapbox GL's zoom is defined against a 512px tile — the whole world is
 * `512 · 2^zoom` pixels across — so the 256-tile form of this formula, which is the one
 * that turns up first in every search, comes out a factor of two wrong.
 */
const metresPerPixel = (zoom, lat) =>
    40075016.686 * Math.cos(lat * Math.PI / 180) / (512 * Math.pow(2, zoom))

/**
 * Smooth interpolation over a table of `[x, z]` — smoothstep between neighbours rather than
 * straight lines, so the bonnet runs into the windscreen as a curve instead of a crease.
 */
const alongCar = (table) => (x) => {
    if (x <= table[0][0]) return table[0][1]
    for (let i = 1; i < table.length; i += 1) {
        if (x <= table[i][0]) {
            const [x0, z0] = table[i - 1]
            const [x1, z1] = table[i]
            const t = (x - x0) / (x1 - x0)
            return z0 + (z1 - z0) * (t * t * (3 - 2 * t))
        }
    }
    return table[table.length - 1][1]
}

/**
 * Build the layer. Resolves once three, the loader and the sweep are all done — so the
 * caller can add it to the map knowing its first frame will draw something.
 *
 * @param {Function} getPose Read EVERY FRAME, never captured. Returns
 *        `{lng, lat, heading, moving}` or null. The car's position is being eased by
 *        `RouteMap`'s own animation loop, and the layer's job is to draw wherever that loop
 *        has the car right now — a pose passed in at construction would be a still. Null
 *        draws nothing, which is how the caller switches this off.
 */
export async function createCarModelLayer({getPose}) {
    const THREE = await import('three')
    const {SVGLoader} = await import('three/examples/jsm/loaders/SVGLoader.js')

    /* ⚠️ THE FILTER ATTRIBUTES ARE STRIPPED FIRST. `map-car.svg` hangs eight inner-shadow
       and drop-shadow filters off its groups, and SVGLoader warns "Filters are not
       supported" once for each — eight lines of console noise on a screen where the console
       is how a driver's problem gets diagnosed. They are a 2D shading trick with nothing to
       contribute to a solid that has real light on it. The flat marker imports the file
       separately and keeps every one of them. */
    const parsed = new SVGLoader().parse(carTopSvg.replace(/\sfilter="[^"]*"/g, ''))
    const hullPath = parsed.paths.find((p) => (p.userData?.style?.fill || '').includes(HULL_FILL))
    if (!hullPath) throw new Error('map-car.svg: no silhouette contour to build the plan from')

    /*
     * FROM THE SVG'S PAGE INTO THE CAR'S OWN FRAME — nose down +X, y to the north.
     *
     * ⚠️ `y` IS NEGATED: the SVG's y runs down the page and this frame's runs north.
     * ⚠️ AND THE NOSE IS TURNED TO +X. `CAR_NOSE_DEG` is a bearing — clockwise from north —
     * while this frame measures anticlockwise from east, which is where the heading rotation
     * in `render` expects to find the nose. `deg − 90` is the whole conversion; dropping it
     * leaves every car on the map crabbing sideways down the road at a fixed 69°.
     */
    const turn = (CAR_NOSE_DEG - 90) * Math.PI / 180
    const cos = Math.cos(turn)
    const sin = Math.sin(turn)
    const outline = hullPath.toShapes()[0].getPoints(CURVE_SAMPLES)
        .map((p) => new THREE.Vector2(p.x * cos + p.y * sin, p.x * sin - p.y * cos))
    // `getPoints` repeats the first point to close the loop; the sweep wants an open list.
    if (outline[0].distanceTo(outline[outline.length - 1]) < 1e-6) outline.pop()

    // One unit long, centred over its own footprint — so `render` scales by metres alone.
    const bounds = new THREE.Box2().setFromPoints(outline)
    const centre = bounds.getCenter(new THREE.Vector2())
    const span = bounds.max.x - bounds.min.x
    const plan = outline.map((p) => new THREE.Vector2((p.x - centre.x) / span, (p.y - centre.y) / span))

    /**
     * THE PLAN, READ AS A FUNCTION — how wide the car is at a given station, straight off
     * the artwork's outline by intersecting it with a vertical line.
     *
     * ⚠️ THE CENTRELINE IS RETURNED TOO, not assumed to be zero. The artwork is drawn on a
     * slant and its outline is not perfectly symmetric about its own axis; forcing symmetry
     * would visibly narrow one flank against the sprite it is meant to match.
     */
    const planAt = (x) => {
        let lo = Infinity
        let hi = -Infinity
        for (let i = 0; i < plan.length; i += 1) {
            const a = plan[i]
            const b = plan[(i + 1) % plan.length]
            if ((a.x <= x && b.x >= x) || (b.x <= x && a.x >= x)) {
                if (Math.abs(b.x - a.x) < 1e-9) {
                    lo = Math.min(lo, a.y, b.y)
                    hi = Math.max(hi, a.y, b.y)
                    continue
                }
                const y = a.y + (b.y - a.y) * (x - a.x) / (b.x - a.x)
                lo = Math.min(lo, y)
                hi = Math.max(hi, y)
            }
        }
        return lo > hi ? null : {half: (hi - lo) / 2, mid: (hi + lo) / 2}
    }

    const roofAt = alongCar(ROOFLINE)
    const floorAt = alongCar(UNDERSIDE)

    const triangle = (into, a, b, c) => into.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z)

    /** One cross-section: a superellipse `half` wide and spanning `zLo…zHi`. */
    const section = (x, half, mid, zLo, zHi) => {
        const cz = (zLo + zHi) / 2
        const hz = (zHi - zLo) / 2
        const round = (v) => Math.sign(v) * Math.pow(Math.abs(v), 2 / SECTION_POWER)
        const points = []
        for (let k = 0; k < SECTION_SIDES; k += 1) {
            const t = 2 * Math.PI * k / SECTION_SIDES
            points.push(new THREE.Vector3(x, mid + half * round(Math.cos(t)), cz + hz * round(Math.sin(t))))
        }
        return points
    }

    /**
     * Skin consecutive sections into a tube, optionally closed at either end with a fan to
     * a tip point. Non-indexed, which is what gives it FLAT normals from
     * `computeVertexNormals` — and flat is right here: smoothing across the shoulder would
     * round away the crease that tells the eye the flank has turned over.
     */
    const sweep = (sections, tipLow, tipHigh) => {
        const out = []
        for (let s = 0; s < sections.length - 1; s += 1) {
            const near = sections[s]
            const far = sections[s + 1]
            for (let i = 0; i < SECTION_SIDES; i += 1) {
                const j = (i + 1) % SECTION_SIDES
                triangle(out, near[i], near[j], far[j])
                triangle(out, near[i], far[j], far[i])
            }
        }
        if (tipLow) {
            const end = sections[0]
            for (let i = 0; i < SECTION_SIDES; i += 1) triangle(out, tipLow, end[(i + 1) % SECTION_SIDES], end[i])
        }
        if (tipHigh) {
            const end = sections[sections.length - 1]
            for (let i = 0; i < SECTION_SIDES; i += 1) triangle(out, tipHigh, end[i], end[(i + 1) % SECTION_SIDES])
        }
        return out
    }

    /* ── THE BODY ─────────────────────────────────────────────────────────────────
       Every station from nose to tail, floor to belt line — or to the roofline where that
       is lower, which is what gives the bonnet and boot their fall rather than leaving them
       as decks at one height. Held a hair short of both ends, where the plan's half-width
       reaches zero and a section would collapse to a line; the tips close it instead. */
    const inset = 0.012
    const bodySections = []
    for (let i = 0; i <= STATIONS; i += 1) {
        const x = -0.5 + inset + (1 - 2 * inset) * (i / STATIONS)
        const at = planAt(x)
        if (at) bodySections.push(section(x, at.half, at.mid, floorAt(x), Math.min(roofAt(x), BELT_Z)))
    }
    const axis = planAt(0).mid
    const tipOf = (x) => new THREE.Vector3(x, axis, (floorAt(x) + Math.min(roofAt(x), BELT_Z)) / 2)
    const body = sweep(bodySections, tipOf(-0.5), tipOf(0.5))

    /* ── THE GREENHOUSE ───────────────────────────────────────────────────────────
       Exactly where the roofline stands above the belt, which is what makes the cabin's
       extent a consequence of the profile rather than a second set of numbers to keep in
       step with it. Started a touch BELOW the belt so it buries its own bottom edge in the
       body instead of showing a seam. */
    const cabinSections = []
    for (let i = 0; i <= STATIONS; i += 1) {
        const x = -0.5 + i / STATIONS
        const at = planAt(x)
        if (at && roofAt(x) > BELT_Z + 1e-4) {
            cabinSections.push(section(x, at.half * GLASS_INSET, at.mid, BELT_Z - 0.012, roofAt(x)))
        }
    }
    const glass = sweep(cabinSections, null, null)

    /* ── THE ROOF PLATE ───────────────────────────────────────────────────────────
       A light lid over the flat top of the greenhouse — the one part of this car that is
       carried straight over from the sprite, whose bright top patch is the thing a driver
       recognises from the 2D view. */
    const roof = []
    const plateStations = []
    for (let i = 0; i <= STATIONS; i += 1) {
        const x = -0.5 + i / STATIONS
        if (roofAt(x) >= ROOF_MIN_Z) plateStations.push(x)
    }
    for (let i = 0; i < plateStations.length - 1; i += 1) {
        const xa = plateStations[i]
        const xb = plateStations[i + 1]
        const a = planAt(xa)
        const b = planAt(xb)
        const ha = a.half * ROOF_INSET
        const hb = b.half * ROOF_INSET
        const za = roofAt(xa) + 0.001
        const zb = roofAt(xb) + 0.001
        const a0 = new THREE.Vector3(xa, a.mid - ha, za)
        const a1 = new THREE.Vector3(xa, a.mid + ha, za)
        const b0 = new THREE.Vector3(xb, b.mid - hb, zb)
        const b1 = new THREE.Vector3(xb, b.mid + hb, zb)
        triangle(roof, a0, b0, b1)
        triangle(roof, a0, b1, a1)
    }

    /* ── THE WHEELS ───────────────────────────────────────────────────────────────
       Four short cylinders, and worth their 192 triangles: they are what puts the car ON
       the road. Without them the body floats, and a floating shape at this size reads as a
       marker rather than a vehicle — which is precisely what the 3D car exists to stop. */
    const wheels = []
    for (const endSign of [-1, 1]) {
        for (const sideSign of [-1, 1]) {
            const x = endSign * WHEEL.axleX
            const at = planAt(x)
            if (!at) continue
            const y = at.mid + sideSign * at.half * WHEEL.yInset
            const rims = [[], []]
            for (let k = 0; k < WHEEL.sides; k += 1) {
                const t = 2 * Math.PI * k / WHEEL.sides
                const dx = Math.cos(t) * WHEEL.radius
                const dz = Math.sin(t) * WHEEL.radius
                rims[0].push(new THREE.Vector3(x + dx, y - WHEEL.width / 2, WHEEL.radius + dz))
                rims[1].push(new THREE.Vector3(x + dx, y + WHEEL.width / 2, WHEEL.radius + dz))
            }
            for (let k = 0; k < WHEEL.sides; k += 1) {
                const j = (k + 1) % WHEEL.sides
                triangle(wheels, rims[0][k], rims[0][j], rims[1][j])
                triangle(wheels, rims[0][k], rims[1][j], rims[1][k])
            }
            for (const face of [0, 1]) {
                const hub = new THREE.Vector3(x, y + (face ? 1 : -1) * WHEEL.width / 2, WHEEL.radius)
                for (let k = 0; k < WHEEL.sides; k += 1) {
                    const j = (k + 1) % WHEEL.sides
                    if (face) triangle(wheels, hub, rims[1][k], rims[1][j])
                    else triangle(wheels, hub, rims[0][j], rims[0][k])
                }
            }
        }
    }

    const material = (color, roughness, metalness) => new THREE.MeshStandardMaterial({
        color,
        roughness,
        metalness,
        /* ⚠️ `DoubleSide`, and it earns its keep twice. The world matrix in `render` mirrors
           in Y and Mapbox's own projection mirrors again, so the winding SHOULD survive —
           but "should" is not good enough to risk an invisible car on the screen a driver
           depends on. It also makes the lighting self-correcting: three flips the normal for
           a back face, so a sweep that came out wound inwards still shades correctly rather
           than going black. */
        side: THREE.DoubleSide,
    })

    const parts = [
        {positions: body, material: material(BODY_COLOR, 0.5, 0.3)},
        {positions: glass, material: material(GLASS_COLOR, 0.12, 0.35)},
        {positions: roof, material: material(ROOF_COLOR, 0.45, 0.25)},
        {positions: wheels, material: material(WHEEL_COLOR, 0.85, 0.05)},
    ]

    const car = new THREE.Group()
    const geometries = []
    const materials = []
    parts.forEach(({positions, material: partMaterial}) => {
        const geometry = new THREE.BufferGeometry()
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
        geometry.computeVertexNormals()
        geometries.push(geometry)
        materials.push(partMaterial)
        car.add(new THREE.Mesh(geometry, partMaterial))
    })

    return {
        id: CAR_MODEL_LAYER_ID,
        type: 'custom',
        /* ⚠️ '3d', not '2d'. It is what gets this layer the depth buffer; on '2d' the car
           would paint flat over everything including the buildings it is driving past. */
        renderingMode: '3d',

        onAdd(map, gl) {
            this.map = map
            /* A bare Camera, because the projection matrix is not ours to compute — Mapbox
               hands us its own each frame and we post-multiply the car's placement onto it.
               A PerspectiveCamera would only overwrite that on the first update. */
            this.camera = new THREE.Camera()
            this.scene = new THREE.Scene()

            /*
             * ⚠️ LIT IN THE CAR'S OWN FRAME, so the light TURNS WITH IT rather than staying
             * put in the world. That is wrong physics and right design: a fixed sun means
             * the car goes flat and unreadable every time the driver turns onto a road
             * facing it, on the one screen where the car has to be findable at a glance.
             * This way the shoulder keeps its highlight through every turn.
             */
            this.scene.add(new THREE.AmbientLight(0xffffff, 2.0))
            const key = new THREE.DirectionalLight(0xffffff, 2.6)
            key.position.set(-0.6, -0.9, 1.4)
            this.scene.add(key)
            const fill = new THREE.DirectionalLight(0xffffff, 1.2)
            fill.position.set(0.9, 0.7, 0.5)
            this.scene.add(fill)

            this.scene.add(car)

            /* ⚠️ MAPBOX'S OWN CANVAS AND CONTEXT. A second canvas would be a second layer of
               the page that knows nothing about the map's depth buffer, so the car could
               not be occluded by anything and would slide over the map rather than move on
               it. `autoClear` off for the same reason: the map has already drawn. */
            this.renderer = new THREE.WebGLRenderer({canvas: map.getCanvas(), context: gl, antialias: true})
            this.renderer.autoClear = false
        },

        onRemove() {
            this.scene?.remove(car)
            materials.forEach((m) => m.dispose())
            geometries.forEach((g) => g.dispose())
            this.renderer?.dispose()
        },

        render(gl, matrix) {
            const pose = getPose()
            if (!pose || !this.renderer) return

            /* v3 hands the matrix straight through; the projected-globe path wraps it. Both
               shapes are read rather than assumed, so a Mapbox upgrade cannot silently blank
               the car. */
            const mat = matrix?.length === 16 ? matrix : matrix?.defaultProjectionData?.mainMatrix
            if (!mat) return

            const mc = mapboxgl.MercatorCoordinate.fromLngLat({lng: pose.lng, lat: pose.lat}, 0)
            // Ground-anchored, with a floor — see `MIN_SCREEN_PX`.
            const floorM = MIN_SCREEN_PX * metresPerPixel(this.map.getZoom(), pose.lat)
            const drawnM = Math.max(DRAWN_LENGTH_M, floorM)
            const scale = mc.meterInMercatorCoordinateUnits() * drawnM

            /*
             * ONTO THE MAP.
             *
             * The car is built one unit long, nose down +X, y-north and z-up. Mercator is
             * x-east / y-SOUTH / z-up, so scaling y by −1 is the whole conversion and the
             * rest is one rotation about the vertical.
             *
             * ⚠️ THE ROTATION IS `90 − heading`, NOT `heading`. A heading is degrees
             * CLOCKWISE FROM NORTH; a rotation about +Z is degrees ANTICLOCKWISE FROM EAST,
             * which is where the nose starts. Getting this backwards gives a car that
             * mirrors every turn — right at a junction, left on the screen.
             */
            const m = new THREE.Matrix4().fromArray(Array.from(mat))
            const l = new THREE.Matrix4()
                .makeTranslation(mc.x, mc.y, mc.z)
                .scale(new THREE.Vector3(scale, -scale, scale))
                .multiply(new THREE.Matrix4().makeRotationZ((90 - pose.heading) * Math.PI / 180))

            this.camera.projectionMatrix = m.multiply(l)

            /* ⚠️ `resetState` FIRST. three and Mapbox share one GL context and both cache
               what they think its state is; without this the car inherits whatever Mapbox
               left bound and paints as garbage — or not at all. */
            this.renderer.resetState()
            this.renderer.render(this.scene, this.camera)

            /*
             * ⚠️ ONLY WHILE THE CAR IS ACTUALLY MOVING. Mapbox repaints when something asks
             * it to, and asking on every frame unconditionally pins a phone at 60fps of GPU
             * for a car parked at a kerb — on the one screen a driver leaves open for the
             * whole ride. The glide between fixes is the only time a new frame shows
             * anything new, so that is the only time this asks for one.
             */
            if (pose.moving) this.map.triggerRepaint()
        },
    }
}
